import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import Library from "./pages/Library";
import Player from "./pages/Player";
import CheckIn from "./pages/CheckIn";
import Journal from "./pages/Journal";
import Profile from "./pages/Profile";
import Paywall from "./pages/Paywall";
import PaymentResult from "./pages/PaymentResult";
import AppLayout from "./components/AppLayout";
import AdminApp from "./pages/admin/AdminApp";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";

const Loader = () => (
  <div className="mm-frame flex items-center justify-center">
    <div className="w-10 h-10 rounded-full border-[3px] border-slate-200 border-t-royal animate-spin" />
  </div>
);

const Protected = ({ children, requireOnboarding = true }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/welcome" replace />;
  if (requireOnboarding && !user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/home" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/welcome" replace />} />
      <Route path="/welcome" element={<Landing />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Protected requireOnboarding={false}><Onboarding /></Protected>} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/home" element={<Home />} />
        <Route path="/library" element={<Library />} />
        <Route path="/checkin" element={<CheckIn />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/profile" element={<Profile />} />
      </Route>
      <Route path="/player/:id" element={<Protected><Player /></Protected>} />
      <Route path="/paywall" element={<Protected><Paywall /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentResult /></Protected>} />
      <Route path="/admin/*" element={<AdminRoute><AdminApp /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/welcome" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => { document.title = "MindMoves.ai — Your MindMoves You"; }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-center" />
      </BrowserRouter>
    </AuthProvider>
  );
}
